import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCurrencyCode } from "../../genericComponents/helper-functions/CurrencySymbol";
import Image from "next/image";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import {
  PAYMENT_MINT,
  PAYMENT_MINT_DARK,
  paymentGradient,
  paymentPrimaryButtonSx,
  cartMobileSurfaceSx,
} from "../payment/paymentUi";

interface AllCarts {
  openAllCarts: any;
  handleCloseAllCarts: any;
  allCarts: any;
  changedCid: any;
}

const AllCarts: React.FC<AllCarts> = ({
  openAllCarts,
  handleCloseAllCarts,
  allCarts,
  changedCid,
}) => {
  const currencyCode = useCurrencyCode();
  const ChangeViewCart = (item: any) => {
    changedCid(item?.cid);
    handleCloseAllCarts();
    localStorage.setItem("cid", btoa(JSON.stringify(item?.cid)));
  };

  return (
    <React.Fragment>
      <Dialog
        open={openAllCarts}
        onClose={handleCloseAllCarts}
        aria-labelledby="all-carts-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "20px" },
            overflow: "hidden",
            border: { xs: "none", sm: `1px solid ${alpha(PAYMENT_MINT, 0.12)}` },
            boxShadow: {
              xs: "none",
              sm: `0 24px 48px ${alpha(PAYMENT_MINT_DARK, 0.12)}`,
            },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: "100%", sm: "min(92vh, 720px)" },
            display: "flex",
            flexDirection: "column",
          },
        }}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(15, 23, 17, 0.45)",
          },
          "@media (max-width: 600px)": {
            "& .MuiDialog-paper": {
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              margin: 0,
            },
          },
        }}
      >
        <Box
          sx={{
            background: paymentGradient,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 2, sm: 2.25 },
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5, minWidth: 0, alignItems: "flex-start" }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  bgcolor: alpha("#fff", 0.18),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ShoppingBagOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  id="all-carts-dialog-title"
                  variant="h6"
                  component="h2"
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.25,
                  }}
                >
                  Your carts
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha("#fff", 0.88),
                    display: "block",
                    mt: 0.5,
                    lineHeight: 1.4,
                  }}
                >
                  Switch between salon carts to continue checkout.
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseAllCarts}
              aria-label="Close"
              sx={{
                color: "#fff",
                bgcolor: alpha("#fff", 0.14),
                "&:hover": { bgcolor: alpha("#fff", 0.24) },
                flexShrink: 0,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            px: { xs: 2, sm: 2.5 },
            py: 2,
            bgcolor: "#fafcfb",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 999,
              background: alpha(PAYMENT_MINT, 0.28),
            },
          }}
        >
          {allCarts?.cartItemRequestsById?.map((item: any, index: any) => (
            <Box
              key={index}
              sx={{
                ...cartMobileSurfaceSx,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: index < allCarts.cartItemRequestsById.length - 1 ? 1.5 : 0,
                border: `1px solid ${alpha(PAYMENT_MINT, 0.12)}`,
                boxShadow: `0 6px 20px ${alpha(PAYMENT_MINT_DARK, 0.06)}`,
                transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                  borderColor: alpha(PAYMENT_MINT, 0.22),
                  boxShadow: `0 10px 28px ${alpha(PAYMENT_MINT_DARK, 0.08)}`,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    mr: 1.5,
                    flexShrink: 0,
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: `1px solid ${alpha(PAYMENT_MINT, 0.1)}`,
                    boxShadow: `0 4px 12px ${alpha(PAYMENT_MINT_DARK, 0.06)}`,
                  }}
                >
                  <Image
                    height={500}
                    width={500}
                    alt="Cart preview"
                    src={item?.cartItemRequests[0]?.ImageUrl}
                    style={{
                      height: "64px",
                      width: "64px",
                      minWidth: "64px",
                      minHeight: "64px",
                      objectFit: "cover",
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontWeight: 600,
                      color: "#0b1710",
                      lineHeight: 1.35,
                      mb: 0.75,
                    }}
                    variant="body2"
                  >
                    {item?.cartItemRequests
                      ?.map((service: any) => service.serviceName)
                      .join(", ")}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 1 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 800,
                        fontVariantNumeric: "tabular-nums",
                        color: PAYMENT_MINT_DARK,
                      }}
                    >
                      {currencyCode}
                      {item?.totalAmount}
                    </Typography>
                    <Typography
                      sx={{
                        textDecoration: "line-through",
                        color: alpha("#0b1710", 0.45),
                        fontSize: "0.8rem",
                      }}
                      variant="body2"
                    >
                      {currencyCode}
                      {item?.totalMrp}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: PAYMENT_MINT,
                      }}
                    >
                      Save {currencyCode}
                      {item?.totalDiscount}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Button
                sx={{
                  ...paymentPrimaryButtonSx,
                  py: 0.65,
                  px: 2,
                  minHeight: 38,
                  fontSize: "0.875rem",
                  flexShrink: 0,
                }}
                variant="contained"
                onClick={() => ChangeViewCart(item)}
              >
                View cart
              </Button>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default AllCarts;
